import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  notification,
  DatePicker,
  Tooltip,
} from 'antd';
import PropTypes from 'prop-types';
import { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import AddWarrantyComponent from './AddWarrantyComponent';
import AddDocumentsCard from './AddDocumentsCard';
import WarrantiesToAdd from './WarrantiesToAdd';
import {
  AppStore,
  CarStore,
  ContactStore,
  CurrentUserStore,
  ShopStore,
} from 'stores';
import { Logger } from 'stores/Classes';
import ContactFormModal from 'components/Contacts/ContactFormModal';
import { isNumber, isNil } from 'lodash';
import { InfoCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';

const DarkHeaderModal = styled(Modal)`
  & {
    width: 60% !important;

    @media (max-width: 1615px) {
      width: 70% !important;
    }

    @media (max-width: 1366px) {
      width: 80% !important;
    }

    @media (max-width: 1024px) {
      width: 90% !important;
    }

    @media (max-width: 768px) {
      width: 95% !important;
    }
  }

    .ant-modal-content {
    .ant-modal-close {
      .ant-modal-close-x {
        color: #05103a;
      }
    }

    .ant-modal-header {

      .ant-modal-title {
        color: #05103a;
      }
    }

  .footer-row {
    margin-top: 21px !important;

    .ant-col {
      display: flex;
      justify-content: flex-end;
    }
  }

  .save-btn {
    margin-left: 8px;
  }
`;

const FloatingMiKmUnit = styled.span`
  position: absolute;
  top: 48%;
  transform: translateY(-50%);
  right: 35px;
  color: #b6b6b6;
`;

const WarrantyForm = styled.div`
  padding: 10px;

  .ant-form {
    .ant-col-12 {
      .ant-form-item {
        margin: 0;
      }
    }

    .ant-form-item-label {
      line-height: 10px;
    }

    .coverage-duration-period {
      margin-top: 4px;
      label {
        visibility: hidden;
      }
    }

    .vendor {
      margin-top: 4px;
    }
  }
`;

const AddWarrantyForm = ({
  onSubmit,
  onClickAddNewContact,
  initialData,
  assets,
  vendors,
  resetForm,
  resetDone,
}) => {
  const [form] = Form.useForm();
  const warrantyComponentsRef = useRef(null);
  const documentsCardRef = useRef(null);

  const miKmUserSettings =
    CurrentUserStore.user?.settings?.odometer === 'km' ? 'km' : 'mi';
  const handleSubmit = async () => {
    try {
      await form.validateFields();

      const values = form.getFieldsValue();
      const components = warrantyComponentsRef.current.getComponents();
      const validComponents = components.filter(
        (component) =>
          component.name.trim() !== '' || component.description.trim() !== ''
      );

      const documents = documentsCardRef.current.getDocuments();

      onSubmit({
        ...values,
        components: validComponents,
        documents,
        id: initialData?.id || null,
        key: initialData?.id || new Date().getTime(),
      });

      // Clear form and components
      form.resetFields();
      warrantyComponentsRef.current.clearComponents();
      documentsCardRef.current.clearDocuments();
    } catch (error) {
      Logger.error('Error adding warranty', error);
      notification.error({
        message: 'Error',
        description: 'An error occurred while adding warranty',
      });
    }
  };

  const handleDateChange = (value) => {
    const duration = form.getFieldValue('coverageDuration');
    const period = form.getFieldValue('coverageDurationPeriod');
    if (duration && period && value) {
      const endDateMoment = value.clone().add(duration, period);
      form.setFieldValue('endDate', endDateMoment);
    }
  };

  const handleDurationChange = (value) => {
    const startDate = form.getFieldValue('startDate');
    if (startDate) {
      const period = form.getFieldValue('coverageDurationPeriod');
      const endDateMoment = startDate.clone().add(value, period);
      form.setFieldValue('endDate', endDateMoment);
    }
  };

  const handlePeriodChange = (value) => {
    const startDate = form.getFieldValue('startDate');
    const duration = form.getFieldValue('coverageDuration');
    if (startDate && duration) {
      const endDateMoment = startDate.clone().add(duration, value);
      form.setFieldValue('endDate', endDateMoment);
    }
  };

  const handleMeterChange = (value) => {
    const range = form.getFieldValue('range');
    if (isNumber(value) && isNumber(range)) {
      form.setFieldValue('endMeter', value + range);
    } else {
      form.setFieldValue('endMeter', null);
    }
  };

  const handleRangeChange = (value) => {
    const startMeter = form.getFieldValue('startMeter');
    if (isNumber(startMeter) && isNumber(value)) {
      form.setFieldValue('endMeter', startMeter + value);
    } else {
      form.setFieldValue('endMeter', null);
    }
  };

  const reset = () => {
    form.resetFields();
    warrantyComponentsRef.current?.clearComponents();
    documentsCardRef.current?.clearDocuments();
    resetDone();
  };

  useEffect(() => {
    if (resetForm) reset();

    if (initialData) {
      form.setFieldValue({
        ...initialData,
      });
    } else {
      form.setFieldsValue({
        assets: initialData?.assets,
        warrantyName: initialData?.warrantyName,
        coverageType: initialData?.coverageType,
        vendor: initialData?.vendor,
        startDate: initialData?.startDate,
        coverageDuration: initialData?.coverageDuration,
        coverageDurationPeriod: initialData?.coverageDurationPeriod || 'years',
        endDate: initialData?.endDate,
        startMeter: initialData?.startMeter,
        range: initialData?.range,
        endMeter: initialData?.endMeter,
      });
    }
  }, [initialData, form]);

  useEffect(() => {
    if (initialData?.vendor) {
      form.setFieldsValue({ vendor: initialData.vendor });
    }
  }, [initialData?.vendor, form]);

  const AssetLabel = (
    <span>
      Select Asset(s)&nbsp;
      <Tooltip title="Select multiple units to apply this warranty. All details, including the starting meter reading, will be applied to all selected assets.">
        <InfoCircleOutlined />
      </Tooltip>
    </span>
  );

  return (
    <WarrantyForm>
      <Form form={form} onFinish={handleSubmit} layout="vertical">
        <Row gutter={[8, 8]}>
          <Col span={6}>
            <Form.Item
              label={AssetLabel}
              name="assets"
              rules={[
                {
                  required: true,
                  message: 'Please select at least one asset',
                  validator: (_, value) => {
                    if (
                      isNil(value) ||
                      value.length === 0 ||
                      (value.length === 1 && isNil(value[0]))
                    ) {
                      return Promise.reject('Please select at least one asset');
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Select
                mode="multiple"
                placeholder="Select assets"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option?.children
                    ?.toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
              >
                {assets.map((asset) => (
                  <Select.Option key={asset.id} value={asset.id}>
                    {asset.carName ||
                      asset.unitId ||
                      asset.make + ' ' + asset.model + ' ' + asset.year}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item
              label="Warranty Name"
              name="warrantyName"
              rules={[{ required: true, message: 'Please name the warranty' }]}
            >
              <Input placeholder="Warranty Name" />
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item
              label="Coverage Type"
              name="coverageType"
              rules={[
                { required: true, message: 'Please select a coverage type' },
              ]}
            >
              <Select placeholder="Select coverage type" allowClear>
                <Select.Option value="standard">Standard</Select.Option>
                <Select.Option value="extended">Extended</Select.Option>
              </Select>
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item label="Vendor" name="vendor" className="vendor">
              <Select
                placeholder="Select vendor"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option?.children
                    ?.toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
              >
                {vendors.map((vendor) => (
                  <Select.Option key={vendor.id} value={vendor.id}>
                    {vendor.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Button
              type="link"
              onClick={onClickAddNewContact}
              icon={<PlusCircleOutlined />}
              style={{
                padding: 0,
                top: '-20px',
              }}
            >
              Add Vendor
            </Button>
          </Col>
        </Row>

        <Row gutter={[8, 8]}>
          <Col span={3}>
            <Form.Item
              label="Start Date"
              name="startDate"
              rules={[{ required: true, message: 'Required' }]}
            >
              <DatePicker
                placeholder="Start Date"
                onChange={handleDateChange}
              />
            </Form.Item>
          </Col>

          <Col span={4}>
            <Form.Item
              label="Coverage Duration"
              name="coverageDuration"
              rules={[
                { required: true, message: 'Required' },
                { max: 1000, type: 'number', message: 'Max 1000' },
                { min: 0, type: 'number', message: 'Min 0' },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Duration"
                onChange={handleDurationChange}
              />
            </Form.Item>
          </Col>

          <Col span={3}>
            <Form.Item
              className="coverage-duration-period"
              label="&nbsp;"
              name="coverageDurationPeriod"
              initialValue="years"
            >
              <Select onChange={handlePeriodChange}>
                <Select.Option value="months">Months</Select.Option>
                <Select.Option value="years">Years</Select.Option>
              </Select>
            </Form.Item>
          </Col>

          <Col span={3}>
            <Form.Item
              label="End Date"
              name="endDate"
              rules={[
                { required: true, message: 'Required' },
                { type: 'date', message: 'Invalid date' },
              ]}
            >
              <DatePicker disabled={true} placeholder="End Date" />
            </Form.Item>
          </Col>

          <Col span={4}>
            <Form.Item
              label="Start Meter"
              name="startMeter"
              rules={[
                { required: true, message: 'Required' },
                { type: 'number', message: 'Invalid number' },
                { max: 1000000, type: 'number', message: 'Max 1M' },
                { min: 0, type: 'number', message: 'Min 0' },
                {
                  validator: (_, value) => {
                    if (value < 0) {
                      return Promise.reject('Invalid number');
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Start Meter"
                onChange={handleMeterChange}
              />
            </Form.Item>
            <FloatingMiKmUnit>{miKmUserSettings}</FloatingMiKmUnit>
          </Col>

          <Col span={3}>
            <Form.Item
              label="Range"
              name="range"
              rules={[
                { required: true, message: 'Required' },
                { type: 'number', message: 'Invalid number' },
                {
                  validator: (_, value) => {
                    if (value < 0) {
                      return Promise.reject('Invalid number');
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Range"
                onChange={handleRangeChange}
              />
            </Form.Item>
            <FloatingMiKmUnit>{miKmUserSettings}</FloatingMiKmUnit>
          </Col>

          <Col span={4}>
            <Form.Item
              label="End Meter"
              name="endMeter"
              rules={[{ required: true, message: 'Required' }]}
            >
              <InputNumber
                disabled={true}
                style={{ width: '100%' }}
                placeholder="End Meter"
              />
            </Form.Item>
            <FloatingMiKmUnit>{miKmUserSettings}</FloatingMiKmUnit>
          </Col>
        </Row>

        <Row gutter={[8, 8]}>
          <Col span={12}>
            <AddWarrantyComponent
              ref={warrantyComponentsRef}
              initialComponents={initialData?.components || []}
            />
          </Col>
          <Col span={12}>
            <AddDocumentsCard
              ref={documentsCardRef}
              documents={initialData?.documents || []}
              onFileUploaded={() => {}}
              overrideUploadProps={{
                accept: 'image/*,.pdf',
              }}
            />
          </Col>
        </Row>

        <Row gutter={[8, 8]}>
          <Col span={24}>
            <Button
              type="primary"
              ghost
              htmlType="submit"
              style={{ float: 'right', marginTop: '8px' }}
            >
              Add
            </Button>
          </Col>
        </Row>
      </Form>
    </WarrantyForm>
  );
};

AddWarrantyForm.propTypes = {
  onSubmit: PropTypes.func,
  onClickAddNewContact: PropTypes.func,
  initialData: PropTypes.object,
  assets: PropTypes.array,
  vendors: PropTypes.array,
  resetForm: PropTypes.bool,
  resetDone: PropTypes.func,
};

const AddWarrantyModal = ({
  visible,
  finishing = false,
  onOk,
  onCancel,
  preSelectedAssets = [],
}) => {
  const [warrantiesToAdd, setWarrantiesToAdd] = useState([]);
  const [assets, setAssets] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [newVendorId, setNewVendorId] = useState(null);
  const [savingContact, setSavingContact] = useState(false);
  const [resetForm, setResetForm] = useState(false);

  const CONTACT_FORM_MODAL_ID = 'ADD_CONTACT_FORM_MODAL';

  // Get the list of assets
  const getAssets = async () => {
    if (!ShopStore.currentShop.id || ShopStore.currentShop.id === -1) {
      setAssets([]);
      return;
    }
    const assets = await CarStore.getShopCarsBasicInfoAndReturn(
      ShopStore.currentShop.id
    );
    setAssets(assets);
  };

  // Get the list of vendors/contacts
  const getVendors = async () => {
    if (!ShopStore.currentShop.id || ShopStore.currentShop.id === -1) {
      setVendors([]);
      return;
    }
    const vendors = await ContactStore.getShopContacts(
      ShopStore.currentShop.id
    );
    setVendors(vendors);
  };

  const onSubmitContactForm = async (data) => {
    try {
      setSavingContact(true);
      const newContact = await ContactStore.createShopContact({
        ...data,
        shopId: ShopStore.currentShop.id,
      });
      AppStore.addSuccess('Vendor saved successfully');
      await getVendors();

      setNewVendorId(newContact.data.id);

      AppStore.openModals.set(CONTACT_FORM_MODAL_ID, false);
    } catch (error) {
      AppStore.addError('Error to save vendor: ' + error.message);
    } finally {
      setSavingContact(false);
    }
  };

  useEffect(() => {
    if (visible) {
      getAssets();
      getVendors();
      setResetForm(true);
    } else {
      setWarrantiesToAdd([]);
    }
  }, [visible]);

  const resetDone = () => {
    setResetForm(false);
  };

  const handleFormSubmit = (data) => {
    const onlyValidAssets = data.assets.filter((assetId) => !isNil(assetId));
    data.assets = onlyValidAssets;
    setWarrantiesToAdd([...warrantiesToAdd, data]);
  };

  return (
    <DarkHeaderModal
      open={visible}
      onCancel={onCancel}
      title="Add Warranty"
      footer={null}
    >
      <Row className="content-row" gutter={[16, 16]}>
        <AddWarrantyForm
          assets={assets}
          vendors={vendors}
          initialData={{
            assets: preSelectedAssets,
            vendor: newVendorId || null,
          }}
          resetForm={resetForm}
          resetDone={resetDone}
          onSubmit={handleFormSubmit}
          onClickAddNewContact={() => {
            AppStore.openModals.set(CONTACT_FORM_MODAL_ID, true);
          }}
        />
        <WarrantiesToAdd
          assets={assets}
          vendors={vendors}
          warranties={warrantiesToAdd}
          onClickDelete={(key) => {
            setWarrantiesToAdd(warrantiesToAdd.filter((w) => w.key !== key));
          }}
          onUpdateWarranty={(data) => {
            setWarrantiesToAdd(
              warrantiesToAdd.map((w) => {
                if (w.key === data.key) {
                  return data;
                }
                return w;
              })
            );
            getVendors();
          }}
          onBulkDeleteWarranties={(selectedKeys) => {
            setWarrantiesToAdd(
              warrantiesToAdd.filter((w) => !selectedKeys.includes(w.key))
            );
          }}
        />
      </Row>
      <Row className="footer-row" gutter={[16, 16]}>
        <Col span={24}>
          <Button onClick={onCancel}>Cancel</Button>
          {/* Show a tooltip if disabled saying that at least one warranty needs to be added */}
          <Tooltip
            title={
              warrantiesToAdd.length === 0
                ? 'At least one warranty needs to be added'
                : ''
            }
          >
            <Button
              className="save-btn"
              type="primary"
              onClick={() => {
                onOk(warrantiesToAdd);
              }}
              loading={finishing}
              disabled={warrantiesToAdd.length === 0}
            >
              Finish
            </Button>
          </Tooltip>
        </Col>
      </Row>
      <ContactFormModal
        modalId={CONTACT_FORM_MODAL_ID}
        saving={savingContact}
        title="Add Vendor"
        defaultData={{}}
        onCancel={() => {
          AppStore.openModals.set(CONTACT_FORM_MODAL_ID, false);
        }}
        onSubmit={onSubmitContactForm}
      />
    </DarkHeaderModal>
  );
};

AddWarrantyModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  finishing: PropTypes.bool,
  onOk: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  preSelectedAssets: PropTypes.array,
};

export default AddWarrantyModal;
